import {isEmpty as _isEmpty, get as _get} from 'lodash';

import {
    stageEvent, stadiumWithCity, getRightsScopesDatesRange, stageEventExists as stageEventExistQuery,
} from '@graphql/stEvent/query';
import {showErrorModal} from '@utils/modal';
import {mapPropertyLicencesForDropdown} from '@utils/propertyLicence';
import {convertToInt} from '@utils/helpers';
import {EVENT_TYPE_STAGE} from '@constants/variables';
import {formatClipProvidersForSubmit} from '@modules/events/utils/formHelper';

import client from '../../../../apolloClient';

export const fetchData = async ({query, variables, errorHeader}) => {
    try {
        const {data} = await client.query({
            query,
            variables,
            fetchPolicy: 'network-only',
        });

        return data;
    } catch (e) {
        showErrorModal({
            header: errorHeader,
            text: e.message,
        });
    }
};

export const fetchStageData = (stageId) =>
    fetchData({
        query: stageEvent,
        variables: {id: stageId},
        errorHeader: 'Error fetching stage event data',
    });

export const fetchStadiumWithCity = (stadiumId) =>
    fetchData({
        query: stadiumWithCity,
        variables: {id: stadiumId},
        errorHeader: 'Error fetching stadium data',
    });

export const fetchRightsScopesDatesRange = (propertyLicenceId) =>
    fetchData({
        query: getRightsScopesDatesRange,
        variables: {id: propertyLicenceId},
        errorHeader: 'Error fetching rights scopes dates range',
    });

export const stageEventExists = (stageId) =>
    fetchData({
        query: stageEventExistQuery,
        variables: {stage: stageId},
        errorHeader: 'Error checking if stage event exists',
    });

export const renderStageCategoryInput = (stage_category = {}) => {
    if (!stage_category) return '';

    const sportName = stage_category?.parent?.sport?.name;
    const firstCategory = stage_category?.parent?.name;
    const secondCategory = stage_category?.name;

    return [sportName, firstCategory, secondCategory].filter(Boolean).join(' > ');
};

export const getStadiumDisplayValue = ({stadium} = {}) => {
    if (!stadium) return '';

    if (stadium?.city?.name) {
        return `${stadium.name} - ${stadium.city.name}`;
    }

    return stadium.name;
};

export const setStageCommonFields = async (setField, data = {}) => {
    const stadium = convertToInt(_get(data, 'stadium.id', null));

    setField('stage_category', {
        defaultValue: renderStageCategoryInput(data.stage_category),
    });

    setField('stage_description', {
        defaultValue: data.description ?? '-',
    });

    if (stadium) {
        const stadiumWithCity = await fetchStadiumWithCity(stadium);

        setField('stadium', {
            defaultValue: getStadiumDisplayValue(stadiumWithCity),
        });
    } else {
        setField('stadium', {
            defaultValue: '-',
        });
    }
};

export const setStageFields = async (setField, data = {}) => {
    await setStageCommonFields(setField, data);

    setField('stage_type', {
        defaultValue: data.stage_type.name,
    });
    setField('stage_name', {
        defaultValue: data.name,
    });
    setField('stage_description', {
        defaultValue: data.description ?? '-',
    });
    setField('start_datetime', {
        defaultValue: data.start_datetime,
    });
    setField('end_datetime', {
        defaultValue: data.end_datetime,
        required: true,
    });
};

export const setPropertyLicenceFieldOptions = (formInstance, prevProps, prevState) => {
    const {
        GraphQLOptionsData: {
            loading: optionsLoading,
            property_licence: propertyLicenceOptions,
            event_status: eventStatusOptions,
        },
    } = formInstance.props;

    if (
        !optionsLoading &&
        optionsLoading !== prevProps.GraphQLOptionsData.loading &&
        !prevState.dataReceived
    ) {
        formInstance.setField('property_licence', {
            options: mapPropertyLicencesForDropdown(propertyLicenceOptions),
        });
        formInstance.setField('event_status', {
            options: eventStatusOptions,
        });
    }
};

export const loadStageDataHelper = (formInstance) => {
    const stageId = formInstance.props.formParams.optionsVariables.stageId;
    formInstance.setState({loading: true});

    fetchStageData(stageId).then((data) => {
        formInstance.setState({
            stageData: data,
            loading: false,
        });
    });
};

export const dataToSave = (formInstance, data) => {
    const savedPresetId = formInstance.state?.defaultForm_data?.event?.event_preset?.id ?? null;
    const {id} = formInstance.state.stageData.stage;

    let dataToSave = {
        event: {
            color: data.color && data.color.substring(1),
            clip_providers: !_isEmpty(data.coverage) ? formatClipProvidersForSubmit(data) : [],
            description: data.description || null,
            end_datetime: data.end_datetime,
            external_description: data.external_description || null,
            event_status: convertToInt(data.event_status),
            event_type: EVENT_TYPE_STAGE,
            property_licence: convertToInt(data.property_licence),
            products: data.coverage || null,
            start_datetime: data.start_datetime,
            notes: data.notes || null,
        },
        stage: id,
        // Inherited fields must be null for stage-linked events; they are read from the stage record.
        name: null,
        description: null,
        stage_category: null,
        stadium: null,
        synchronise_times: !!data.synchronise_times,
    };

    if (data.event_preset) {
        dataToSave.event.event_preset = !data.event_preset
            ? parseInt(savedPresetId) : parseInt(data.event_preset, 10);
    }

    if (undefined !== data.as_additional_contents) {
        dataToSave.as_additional_contents = !!data.as_additional_contents;
    }

    return formInstance.addPresetDataForSubmit(dataToSave, data);
};

export const resetStageDateTimeFields = (setField, stageData) => {
    if (!stageData || !stageData.stage) {
        return;
    }

    const {start_datetime, end_datetime} = stageData.stage;

    setField('start_datetime', {
        defaultValue: start_datetime,
        selected: start_datetime,
    });

    setField('end_datetime', {
        defaultValue: end_datetime,
        selected: end_datetime,
    });
};
